'use client';

import { TimelineEvent, categoryColors } from '@/data/atomHistory';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';

interface TimelineItemProps {
  event: TimelineEvent;
  index: number;
}

export function TimelineItem({ event, index }: TimelineItemProps) {
  const isLeft = index % 2 === 0;
  const categoryColor = categoryColors[event.category];

  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`flex items-center gap-4 md:gap-8 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
    >
      {/* Card */}
      <div className={`flex-1 ${isLeft ? 'md:text-right' : 'md:text-left'} text-left`}>
        <motion.div
          whileHover={{ scale: 1.02, boxShadow: `0 0 30px ${categoryColor}40` }}
          className="dark:bg-[#112240] light:bg-white p-4 md:p-6 rounded-xl dark:border-[#233554] light:border-gray-200 border-2 transition-all"
          style={{ borderColor: categoryColor }}
        >
          {/* Year Badge */}
          <Badge
            className="mb-3 text-xs md:text-sm font-bold"
            style={{
              backgroundColor: `${categoryColor}20`,
              color: categoryColor,
              borderColor: categoryColor
            }}
          >
            {event.year > 0 ? event.year : `${Math.abs(event.year)} av. J.-C.`}
          </Badge>

          {/* Title */}
          <h3 className="text-lg md:text-xl font-bold dark:text-white light:text-gray-900 mb-2">
            {event.title}
          </h3>

          {/* Scientist */}
          <p className="text-sm font-semibold mb-3" style={{ color: categoryColor }}>
            {event.name}
          </p>

          {/* Description */}
          <p className="text-xs md:text-sm dark:text-[#a8b2d1] light:text-gray-600 leading-relaxed">
            {event.description}
          </p>
        </motion.div>
      </div>

      {/* Timeline dot */}
      <div className="relative flex flex-col items-center flex-shrink-0">
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="w-5 h-5 md:w-6 md:h-6 rounded-full border-4 dark:border-[#0a192f] light:border-white z-10"
          style={{ backgroundColor: categoryColor }}
        />
      </div>

      {/* Empty space for alternating layout (hidden on mobile) */}
      <div className="flex-1 hidden md:block" />
    </motion.div>
  );
}
